import { SHELL_SIDEBAR_EXPANDED_MIN_WIDTH } from '@/components/layout/shellBreakpoints';

/**
 * Product-setup checklist (`/welcome`) is desktop-only at the expanded-shell
 * cutoff (1024). Tablets/phones skip `/welcome` and land on `/` instead.
 * Does not change `hasCompletedProductSetup` or tour state.
 */

/** matchMedia query shared by sync helpers and the reactive hook. */
export const WELCOME_VIEWPORT_MEDIA_QUERY = `(min-width: ${SHELL_SIDEBAR_EXPANDED_MIN_WIDTH}px)`;

/**
 * Sync viewport check for redirect-time decisions (outside React).
 * Returns true when the checklist may be shown (≥1024px).
 */
export function isWelcomeViewportWideEnough(): boolean {
  if (typeof window === 'undefined') {
    // Prefer showing welcome when unknown — avoids incorrectly skipping on SSR.
    return true;
  }
  return window.matchMedia(WELCOME_VIEWPORT_MEDIA_QUERY).matches;
}

/**
 * Post-onboarding / revisit-onboarding target: home if setup already done,
 * otherwise `/welcome` only on a wide viewport.
 */
export function getPostOnboardingPath(
  hasCompletedProductSetup: boolean | undefined | null
): string {
  if (hasCompletedProductSetup === true) {
    return '/';
  }
  return isWelcomeViewportWideEnough() ? '/welcome' : '/';
}

/**
 * Navigate target when code would open `/welcome` (settings reopen, packages tour).
 */
export function resolveWelcomeNavigateTarget(): string {
  return isWelcomeViewportWideEnough() ? '/welcome' : '/';
}

/**
 * Remaps a stored/return path that would open `/welcome` to `/` on narrow viewports.
 * Preserves search and hash (e.g. OAuth result query params).
 */
export function remapWelcomePathForViewport(path: string): string {
  if (isWelcomeViewportWideEnough()) {
    return path;
  }

  try {
    const url = new URL(
      path,
      typeof window !== 'undefined' ? window.location.origin : 'http://local'
    );
    if (url.pathname === '/welcome') {
      // `url.search` includes `?`; empty search+hash → `/`
      return `/${url.search}${url.hash}`;
    }
  } catch {
    // Malformed path — leave unchanged
  }

  return path;
}
